import { nativeTheme } from '../native'
import { read, remove, write } from '../storage'

/**
 * Light or dark (Phase 10 plan §2 row 2, §7).
 *
 * The theme follows the OS unless Settings overrides it. It is an attribute,
 * `data-theme` on `<html>`, and `app.css` keys both token tables on it.
 *
 * **The first paint is not decided here.** The inline script in `index.html`
 * sets the attribute before the stylesheet is applied, because a theme applied
 * from the bundle paints the wrong one for as long as the bundle takes to
 * parse, which on a cold phone launch is visible. This module takes over after
 * that: it re-reads the same key, applies the same rule (so the two can never
 * disagree about a stored choice), and keeps it live when the OS flips or
 * somebody picks one. `theme.test.ts` runs the inline script itself against
 * the same cases as this module.
 */

export type ThemeChoice = 'system' | 'light' | 'dark'
export type Theme = 'light' | 'dark'

/** Only an explicit override is stored; System is the key's absence. */
export const KEY_THEME = 'weather.theme'

const DARK_QUERY = '(prefers-color-scheme: dark)'

/**
 * The ground colour per theme, for the browser chrome (`theme-color`). The same
 * two hex values are `--color-bg` in `app.css` and the literals in the inline
 * script in `index.html`; `tokens.test.ts` asserts all three agree.
 */
export const THEME_COLOR: Record<Theme, string> = { light: '#f3f2f2', dark: '#201e1d' }

function systemTheme(): Theme {
  return typeof window !== 'undefined' && window.matchMedia?.(DARK_QUERY).matches
    ? 'dark'
    : 'light'
}

function isChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark'
}

export const theme = $state({
  choice: 'system' as ThemeChoice,
  /** What the OS asks for right now. */
  system: 'light' as Theme,
  /** What is on screen: the choice, or the OS when the choice is System. */
  resolved: 'light' as Theme,
})

function resolve(): void {
  theme.resolved = theme.choice === 'system' ? theme.system : theme.choice
  // Inside the iOS app the status bar and the ground behind the page are the
  // app's; it is told the choice and follows it.
  nativeTheme(theme.choice)
  if (typeof document === 'undefined') return
  const root = document.documentElement
  root.setAttribute('data-theme', theme.resolved)
  // Both theme-color metas carry a media query so the OS picks one before any
  // script runs. An override has to beat the OS, so it writes its colour
  // into both: whichever media matches then shows the chosen theme's.
  const override = theme.choice === 'system' ? null : THEME_COLOR[theme.choice]
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    const media = meta.getAttribute('media') ?? ''
    const own: Theme = media.includes('dark') ? 'dark' : 'light'
    meta.setAttribute('content', override ?? THEME_COLOR[own])
  }
}

/**
 * Read the stored choice and the OS, apply, and watch the OS. Called once,
 * synchronously, before the first render. Returns a teardown.
 */
export function initTheme(): () => void {
  const stored = read(KEY_THEME)
  theme.choice = isChoice(stored) ? stored : 'system'
  theme.system = systemTheme()
  resolve()

  if (typeof window === 'undefined' || !window.matchMedia) return () => {}
  const mq = window.matchMedia(DARK_QUERY)
  const onChange = (e: MediaQueryListEvent) => {
    theme.system = e.matches ? 'dark' : 'light'
    resolve()
  }
  mq.addEventListener?.('change', onChange)
  return () => mq.removeEventListener?.('change', onChange)
}

export function setTheme(choice: ThemeChoice): void {
  theme.choice = choice
  if (choice === 'system') remove(KEY_THEME)
  else write(KEY_THEME, choice)
  resolve()
}
